import type { ReactNode } from 'react';

import { Button, ButtonProps, Dropdown, DropdownItem, OverlayTrigger, Popover } from 'react-bootstrap';
import { useResponsiveDropdownMenuConfig, useWindowDimensions } from 'hooks';
import { ConditionalWrapper } from 'components/conditional_wrapper';
import { Link } from 'react-router-dom';
import cn from 'classnames';

export type DropdownAction = {
    label: string;
    onClick?: () => any;
    to?: string;
}

export type CallToActionProps = ButtonProps & {
    actions?: DropdownAction[]
    fillOnMobile?: boolean;
    label: ReactNode | string;
    popoverContent?: string;
    renderPopover?: boolean;
    to?: string;
}

export const CallToAction = (props: CallToActionProps) => {

    const { actions = [], className, disabled, fillOnMobile = true, label, onClick, popoverContent, renderPopover = false, to } = props;

    const { isBelow: isMobile } = useWindowDimensions({ target: 'md' });
    const popperConfig = useResponsiveDropdownMenuConfig('md');

    const isFilling = fillOnMobile && isMobile;
    const fillClass = isFilling ? 'flex-fill' : undefined;

    // The popover wrapper also needs d-flex so the button's flex-fill has a flex context to fill.
    const wrapperFillClass = isFilling ? 'flex-fill d-flex' : undefined;

    return (
        <ConditionalWrapper
            wrapper={(children => (
                <OverlayTrigger
                    overlay={(
                        <Popover id="cta-tooltip">
                            <Popover.Body>
                                {popoverContent}
                            </Popover.Body>
                        </Popover>
                    )}
                    placement="bottom"
                    trigger={['hover', 'focus']}
                >
                    <div className={cn(className, wrapperFillClass)}>
                        {children}
                    </div>
                </OverlayTrigger>
            ))}
            condition={renderPopover}
        >
            {!actions?.length ?
                (
                    <Button
                        as={to?.length && Link}
                        className={cn('primary-cta d-inline-flex align-items-center gap-2', !renderPopover && className, fillClass)}
                        disabled={disabled}
                        onClick={onClick}
                        to={to}
                    >
                        <div className={'mx-auto mx-md-0'}>
                            {label}
                        </div>
                    </Button>
                )
                :
                (
                    <Dropdown className={cn(wrapperFillClass)}>
                        <Dropdown.Toggle className={cn('primary-cta d-inline-flex gap-2 align-items-center', !renderPopover && className, fillClass)} disabled={disabled}>
                            {label}
                        </Dropdown.Toggle>
                        <Dropdown.Menu renderOnMount popperConfig={popperConfig}>
                            {actions.map(({ label, onClick, to }, key) => (
                                <DropdownItem as={to?.length && Link} key={key} onClick={onClick} to={to}>
                                    {label}
                                </DropdownItem>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                )
            }

        </ConditionalWrapper>
    );

};